import {
  PLAN_FEATURES,
  PLAN_LIMITS,
  PlanFeature,
  PlanLimit,
  SubscriptionPlan
} from '@next-is-free-monorepo/next-is-free-types';

/**
 * Check if a given feature is enabled for a subscription plan.
 *
 * A feature is considered "enabled" if its value is:
 * - `true` → feature available
 * - `number` → feature available with a numeric limit
 * - `"unlimited"` → feature fully available without limit
 *
 * @param plan - The subscription plan to check
 * @param feature - The feature key to check against PLAN_FEATURES
 * @returns true if the feature is enabled in any form, false otherwise
 *
 * @example
 * planHasFeature("starter", "accessCustomers"); // false
 * planHasFeature("essential", "accessCustomers"); // true
 */
export function planFeatures(
  plan: SubscriptionPlan,
  feature: PlanFeature
): boolean {
  return PLAN_FEATURES[plan][feature];
}

/**
 * Get the numeric limit for a given feature in a subscription plan.
 *
 * A feature may be:
 * - `number` → returns that numeric limit
 * - `"unlimited"` → returns `Infinity`
 * - `true`/`false` → returns null (no numeric limit defined)
 *
 * @param plan - The subscription plan to check
 * @param feature - The feature key to check against PLAN_FEATURES
 * @returns the limit as number, Infinity if unlimited, or null if not applicable
 *
 * @example
 * planGetLimit("starter", "maxCustomers"); // 50
 * planGetLimit("enterprise", "maxCustomers"); // Infinity
 * planGetLimit("essential", "analytics"); // null
 */
export function planGetLimit(
  plan: SubscriptionPlan,
  feature: PlanLimit
): number {
  const val = PLAN_LIMITS[plan][feature];
  if (typeof val === "number"){
    return val
  }else {
    return Infinity
  }
}


export function isPlanFeature(key: string): key is PlanFeature {
  return key in PLAN_FEATURES.starter;
}

export function isPlanLimit(key: string): key is PlanLimit {
  return key in PLAN_LIMITS.starter;
}
